import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/store/auth";
import { api } from "@/lib/api";
import ProductManager from "@/components/ProductManager";
import BlogManager from "@/components/BlogManager";
import CampaignsPanel from "@/components/CampaignsPanel";
import HeroesPanel from "@/components/HeroesPanel";
import SeoPanel from "@/components/SeoPanel";
import {
  LogOut, Plus, Trash2, TrendingUp, DollarSign, ShoppingBag, Users, Clock, Calendar,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-6" data-testid={`stat-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className="flex items-center gap-3 text-white/50 text-xs uppercase tracking-widest mb-3">
        <Icon size={14} className={accent || "text-[#FF3D8B]"} /> {label}
      </div>
      <div className="font-display text-4xl text-white leading-none">{value}</div>
      {sub && <div className="text-white/40 text-xs mt-2">{sub}</div>}
    </div>
  );
}

const TABS = ["Overview", "Products", "Blog", "Campaigns", "Heroes", "SEO", "Coupons", "Orders", "Subscribers"];

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("Overview");
  const [stats, setStats] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [subs, setSubs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [newCoupon, setNewCoupon] = useState({ code: "", percent_off: 10, min_order: 0 });
  const [couponError, setCouponError] = useState(null);

  const reload = async () => {
    const [s, c, n, o] = await Promise.all([
      api.get("/admin/analytics", { withCredentials: true }),
      api.get("/admin/coupons", { withCredentials: true }),
      api.get("/admin/newsletter", { withCredentials: true }),
      api.get("/admin/orders", { withCredentials: true }),
    ]);
    setStats(s.data); setCoupons(c.data.coupons); setSubs(n.data.subscribers); setOrders(o.data.orders);
  };

  useEffect(() => {
    if (user?.role === "owner") reload();
  }, [user]);

  if (loading) return <div className="p-24 text-white/50">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "owner") return <Navigate to="/" replace />;

  const addCoupon = async (e) => {
    e.preventDefault(); setCouponError(null);
    try {
      await api.post("/admin/coupons", newCoupon, { withCredentials: true });
      setNewCoupon({ code: "", percent_off: 10, min_order: 0 });
      reload();
    } catch (err) {
      setCouponError(err?.response?.data?.detail || "Failed");
    }
  };
  const toggle = async (code) => { await api.patch(`/admin/coupons/${code}`, {}, { withCredentials: true }); reload(); };
  const del = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    await api.delete(`/admin/coupons/${code}`, { withCredentials: true });
    reload();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10" data-testid="admin-dashboard-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[#A855F7] uppercase text-xs tracking-widest mb-2">Owner Console</div>
          <h1 className="font-display text-5xl text-white">Dashboard</h1>
          <div className="text-white/40 text-xs mt-1">{user.email}</div>
        </div>
        <button
          onClick={async () => { await logout(); nav("/"); }}
          data-testid="admin-logout-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-white/70 hover:border-[#FF3D8B] hover:text-[#FF3D8B] transition-colors text-sm"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-white/10 mb-8">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`admin-tab-${t.toLowerCase()}`}
            className={`px-4 py-2.5 text-xs uppercase tracking-widest transition-colors ${
              tab === t ? "text-[#FF3D8B] border-b-2 border-[#FF3D8B]" : "text-white/50 hover:text-white"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Calendar} label="Today" value={`$${stats.revenue_today.toFixed(2)}`} sub="Revenue" />
            <StatCard icon={DollarSign} label="This Month" value={`$${stats.revenue_month.toFixed(2)}`} sub="Revenue" />
            <StatCard icon={TrendingUp} label="Year to Date" value={`$${stats.revenue_ytd.toFixed(2)}`} sub={`${stats.orders_paid} orders`} />
            <StatCard icon={ShoppingBag} label="All Time" value={`$${stats.revenue_all.toFixed(2)}`} sub={`AOV $${stats.avg_order_value}`} />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={TrendingUp} label="Conversion" value={`${stats.conversion_rate}%`} sub={`${stats.orders_paid}/${stats.orders_initiated} sessions`} />
            <StatCard icon={Clock} label="Abandoned" value={stats.abandoned_count} sub="carts in progress" accent="text-[#A855F7]" />
            <StatCard icon={Users} label="Users" value={stats.total_users} sub="registered" accent="text-[#A855F7]" />
            <StatCard icon={Users} label="Newsletter" value={stats.newsletter_count} sub="subscribers" accent="text-[#A855F7]" />
          </div>

          <div className="grid lg:grid-cols-3 gap-6 mb-10">
            <div className="lg:col-span-2 rounded-xl border border-white/10 bg-[#0f0f0f] p-6">
              <div className="text-white/60 text-xs uppercase tracking-widest mb-4">Revenue — Last 14 days</div>
              {stats.daily_series.length === 0 ? (
                <div className="text-white/40 text-sm h-64 flex items-center justify-center">
                  No paid orders yet. Once orders come in, revenue lands here.
                </div>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.daily_series}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                      <XAxis dataKey="date" stroke="#666" style={{ fontSize: 11 }} />
                      <YAxis stroke="#666" style={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ background: "#111", border: "1px solid #333" }} />
                      <Line type="monotone" dataKey="revenue" stroke="#FF3D8B" strokeWidth={2} dot={{ fill: "#FF3D8B" }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-6">
              <div className="text-white/60 text-xs uppercase tracking-widest mb-4">Top Scents</div>
              {stats.top_scents.length === 0 ? (
                <div className="text-white/40 text-sm">No sales yet.</div>
              ) : (
                <ul className="space-y-3">
                  {stats.top_scents.map((s, idx) => (
                    <li key={s.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-[#FF3D8B] font-display text-lg w-6">{idx + 1}</span>
                        <span className="text-white text-sm">{s.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-white text-sm font-semibold">{s.quantity} sold</div>
                        <div className="text-white/40 text-xs">${s.revenue}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <section className="mb-8" data-testid="abandoned-carts-section">
            <h2 className="font-display text-2xl text-white mb-5">Abandoned Carts</h2>
            <div className="rounded-xl border border-white/10 bg-[#0f0f0f] overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-white/50 text-[11px] uppercase tracking-widest">
                  <tr>
                    <th className="text-left p-4">Cart ID</th>
                    <th className="text-left p-4">User</th>
                    <th className="text-left p-4">Items</th>
                    <th className="text-left p-4">Sitting for</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.abandoned_carts.map((c) => {
                    const mins = c.minutes_since_activity;
                    const label =
                      mins < 60 ? `${mins}m` :
                      mins < 60 * 24 ? `${Math.round(mins / 60)}h` :
                      `${Math.round(mins / (60 * 24))}d`;
                    return (
                      <tr key={c.cart_id} className="border-t border-white/5">
                        <td className="p-4 text-white/60 font-mono text-xs">{c.cart_id.slice(0, 12)}…</td>
                        <td className="p-4 text-white/70">{c.user_email || "anonymous"}</td>
                        <td className="p-4 text-white/80">
                          {c.items.map((i, idx) => (
                            <span key={`${c.cart_id}-${i.product_id || idx}`}>{i.name} × {i.quantity}{idx < c.items.length - 1 ? ", " : ""}</span>
                          ))}
                        </td>
                        <td className="p-4">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            mins > 60 * 24 ? "bg-red-500/20 text-red-400" :
                            mins > 60 ? "bg-yellow-500/20 text-yellow-400" :
                            "bg-white/5 text-white/60"
                          }`}>{label}</span>
                        </td>
                      </tr>
                    );
                  })}
                  {stats.abandoned_carts.length === 0 && (
                    <tr><td colSpan={4} className="p-6 text-center text-white/40 text-sm">No abandoned carts.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {tab === "Products" && <ProductManager />}

      {tab === "Blog" && <BlogManager />}

      {tab === "Campaigns" && <CampaignsPanel />}

      {tab === "Heroes" && <HeroesPanel />}

      {tab === "SEO" && <SeoPanel />}

      {tab === "Coupons" && (
        <section className="mb-12">
          <h2 className="font-display text-2xl text-white mb-5">Coupons</h2>
          <form onSubmit={addCoupon} className="rounded-xl border border-white/10 bg-[#0f0f0f] p-5 flex flex-wrap items-end gap-4 mb-4">
            <div>
              <label className="block text-white/50 text-[11px] uppercase tracking-widest mb-1">Code</label>
              <input value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value })}
                required placeholder="CHERRY10" data-testid="new-coupon-code"
                className="bg-white/5 border border-white/15 rounded-md px-3 py-2 text-white text-sm w-40 focus:outline-none focus:border-[#FF3D8B]" />
            </div>
            <div>
              <label className="block text-white/50 text-[11px] uppercase tracking-widest mb-1">% Off</label>
              <input type="number" min="1" max="100" value={newCoupon.percent_off}
                onChange={(e) => setNewCoupon({ ...newCoupon, percent_off: parseInt(e.target.value || 0) })}
                data-testid="new-coupon-percent"
                className="bg-white/5 border border-white/15 rounded-md px-3 py-2 text-white text-sm w-24 focus:outline-none focus:border-[#FF3D8B]" />
            </div>
            <div>
              <label className="block text-white/50 text-[11px] uppercase tracking-widest mb-1">Min Order $</label>
              <input type="number" min="0" step="0.01" value={newCoupon.min_order}
                onChange={(e) => setNewCoupon({ ...newCoupon, min_order: parseFloat(e.target.value || 0) })}
                data-testid="new-coupon-min"
                className="bg-white/5 border border-white/15 rounded-md px-3 py-2 text-white text-sm w-28 focus:outline-none focus:border-[#FF3D8B]" />
            </div>
            <button type="submit" data-testid="new-coupon-submit"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#ff5ba0]">
              <Plus size={14} /> Add
            </button>
            {couponError && <div className="text-red-400 text-xs">{couponError}</div>}
          </form>
          <div className="rounded-xl border border-white/10 bg-[#0f0f0f] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-white/50 text-[11px] uppercase tracking-widest">
                <tr><th className="text-left p-4">Code</th><th className="text-left p-4">Off</th><th className="text-left p-4">Min</th><th className="text-left p-4">Status</th><th></th></tr>
              </thead>
              <tbody>
                {coupons.map((c) => (
                  <tr key={c.code} className="border-t border-white/5" data-testid={`coupon-row-${c.code}`}>
                    <td className="p-4 font-semibold text-white">{c.code}</td>
                    <td className="p-4 text-[#FF3D8B]">{c.percent_off}%</td>
                    <td className="p-4 text-white/70">${c.min_order.toFixed(2)}</td>
                    <td className="p-4">
                      <button onClick={() => toggle(c.code)} className={`text-xs px-3 py-1 rounded-full uppercase tracking-wider ${c.active ? "bg-[#FF3D8B]/20 text-[#FF3D8B]" : "bg-white/5 text-white/40"}`}>
                        {c.active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="p-4 text-right">
                      <button onClick={() => del(c.code)} className="text-white/40 hover:text-red-400"><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
                {coupons.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-white/40 text-sm">No coupons yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === "Orders" && (
        <section className="mb-12">
          <h2 className="font-display text-2xl text-white mb-5">Recent Orders</h2>
          <div className="rounded-xl border border-white/10 bg-[#0f0f0f] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-white/50 text-[11px] uppercase tracking-widest">
                <tr>
                  <th className="text-left p-4">When</th><th className="text-left p-4">Email</th>
                  <th className="text-left p-4">Items</th><th className="text-left p-4">Amount</th>
                  <th className="text-left p-4">Status</th><th className="text-left p-4">Coupon</th>
                </tr>
              </thead>
              <tbody>
                {orders.slice(0, 30).map((o) => (
                  <tr key={o.session_id} className="border-t border-white/5">
                    <td className="p-4 text-white/50 text-xs whitespace-nowrap">{(o.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="p-4 text-white/70">{o.customer_email || "—"}</td>
                    <td className="p-4 text-white/70 text-xs">
                      {(o.item_details || []).map((i, idx) => (
                        <span key={`${o.session_id}-${i.product_id || idx}`}>{i.name} ×{i.quantity}{idx < o.item_details.length - 1 ? ", " : ""}</span>
                      ))}
                    </td>
                    <td className="p-4 text-white font-semibold">${o.amount?.toFixed(2)}</td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${o.payment_status === "paid" ? "bg-[#FF3D8B]/20 text-[#FF3D8B]" : "bg-white/5 text-white/50"}`}>
                        {o.payment_status}
                      </span>
                    </td>
                    <td className="p-4 text-white/60">{o.coupon_code || "—"}</td>
                  </tr>
                ))}
                {orders.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-white/40 text-sm">No orders yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === "Subscribers" && (
        <section>
          <h2 className="font-display text-2xl text-white mb-5">Newsletter Subscribers ({subs.length})</h2>
          <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-5 max-h-[600px] overflow-y-auto">
            {subs.length === 0 ? (
              <div className="text-white/40 text-sm">No signups yet.</div>
            ) : (
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                {subs.map((s) => (
                  <li key={s.email} className="flex justify-between border-b border-white/5 py-1.5">
                    <span className="text-white/80">{s.email}</span>
                    <span className="text-white/30 text-xs uppercase tracking-wider">{s.source}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
